/**
 * Hit-FX engine — plays a resolved HitFxPlan as a one-shot particle
 * burst of CARTRIDGE battle-anim frames at the defender, plus the
 * hit-stop freeze for super/crit impacts.
 *
 * The pixels are lifted from pokefirered's battle_anims sheets
 * (extract-battlefx-cli → assets/battlefx/ + battleFxManifest); the
 * MOTION is ours — Phaser emitter params per skin, not a port of the
 * GBA animation VM. Zone 3 stays pure: frames render unfiltered at
 * pixel scale, no glow, no gradients.
 *
 * Determinism note: Phaser's particle emitter jitters positions/speeds
 * with its own internal RNG. That's render-only state — the seeded
 * gameplay domains (seedStore) are untouched, same posture as tween
 * easing. Replays reproduce EVENTS, not particle positions.
 */
import Phaser from 'phaser';
import { BATTLE_FX_MANIFEST } from '../data/battleFxManifest';
import type { FxMotion, HitFxPlan } from './hitFxConfig';

/** Loader hook — call from BattleScene.preload(). Idempotent per the
 *  Phaser texture cache (re-entering battle skips loaded keys). */
export function preloadBattleFx(load: Phaser.Loader.LoaderPlugin): void {
  for (const [id, sheet] of Object.entries(BATTLE_FX_MANIFEST)) {
    load.spritesheet(`fx-${id}`, `assets/battlefx/${id}.png`, {
      frameWidth: sheet.frameWidth,
      frameHeight: sheet.frameHeight,
    });
  }
}

/** Alternator for slash-band mirroring — see playHitFx. */
let slashFlip = false;

/** Angle bands per motion family, degrees (Phaser: 0 = right, 90 = down). */
function motionAngles(motion: FxMotion): { min: number; max: number } {
  switch (motion) {
    case 'rise':
      return { min: 245, max: 295 }; // upward cone
    case 'fall':
      return { min: 210, max: 330 }; // up-and-out, gravity brings it down
    case 'spray':
      return { min: 150, max: 390 }; // low horizontal fan (150-210 + -30-30)
    case 'slash':
      return { min: -25, max: 25 }; // one sharp band; mirrored per-burst below
    case 'ring':
    case 'burst':
      return { min: 0, max: 360 };
  }
}

/**
 * One-shot burst at (x, y) in scene coords. The emitter destroys
 * itself after the longest particle dies — no pooling needed at ≤20
 * particles per burst with bursts ≥1s apart (the damage handler's HP
 * drain await paces them).
 */
/** Global intensity pass (playtest round 2: Bubble's burst was "very
 *  small, almost unnoticeable"). The authored per-skin values preserve
 *  RELATIVE balance; these boosts scale everything up to read beside
 *  battler sprites rendered at ~2.5x. Spawn spread widens the burst to
 *  cover the sprite body instead of a single center point. */
export const FX_SCALE_BOOST = 2.1;
export const FX_SPEED_BOOST = 1.5;
export const FX_LIFESPAN_BOOST = 1.35;
export const FX_COUNT_BOOST = 1.5;
export const FX_SPAWN_RADIUS_PX = 22;

const warnedMissing = new Set<string>();

export function playHitFx(
  scene: Phaser.Scene,
  x: number,
  y: number,
  plan: HitFxPlan,
  /** Spatial multiplier for scale, burst speed, and spawn radius.
   *  1 = BattleScene's 4x-still staging (the tuning baseline). The
   *  in-world duel host passes its battler screen-size ratio so
   *  bursts hug 32px walkers instead of swallowing the whole frame
   *  at duel zoom (host seam, phone dogfood 2026-08-03). */
  sizeScale = 1,
): boolean {
  const { skin, mods } = plan;
  const sheet = BATTLE_FX_MANIFEST[skin.sheet];
  if (!sheet) return false; // manifest drift — FX are decoration, never throw
  const textureKey = `fx-${skin.sheet}`;
  if (!scene.textures.exists(textureKey)) {
    // Load failure hardening (round-2 investigation): signal the caller
    // so the impact still reads via the tint-flash fallback instead of
    // silently showing nothing. Warn once per key.
    if (!warnedMissing.has(textureKey)) {
      warnedMissing.add(textureKey);
      console.warn('[hitFx] texture missing, falling back to flash:', textureKey);
    }
    return false;
  }

  const angles = motionAngles(skin.motion);
  // Slash bursts alternate the band's side so claw/gust sweeps read as
  // crossing the sprite rather than always exiting stage right. A
  // deterministic toggle (not Math.random) — the only render-side RNG
  // in this module stays Phaser's internal emitter jitter.
  const flip = skin.motion === 'slash' && (slashFlip = !slashFlip);
  const angleMin = flip ? angles.min + 180 : angles.min;
  const angleMax = flip ? angles.max + 180 : angles.max;

  const frames = Array.from({ length: sheet.frameCount }, (_, i) => i);
  const scaleStart = skin.scale * mods.scaleMul * FX_SCALE_BOOST * sizeScale;
  const lifespan = Math.round(skin.lifespan * FX_LIFESPAN_BOOST);

  const emitter = scene.add.particles(x, y, textureKey, {
    frame: frames,
    lifespan,
    speed: {
      min: skin.speed[0] * mods.speedMul * FX_SPEED_BOOST * sizeScale,
      max: skin.speed[1] * mods.speedMul * FX_SPEED_BOOST * sizeScale,
    },
    angle: { min: angleMin, max: angleMax },
    gravityY: skin.gravityY,
    // Hold near-full scale then collapse at end-of-life — GBA bursts
    // pop out and vanish; they don't balloon.
    scale: { start: scaleStart, end: scaleStart * 0.55 },
    alpha: { start: mods.alpha, end: 0 },
    rotate: skin.rotate ? { min: 0, max: 360 } : 0,
    emitting: false,
    // Spawn across the sprite body, not a single center point.
    emitZone: {
      type: 'random',
      source: new Phaser.Geom.Circle(0, 0, FX_SPAWN_RADIUS_PX * sizeScale),
      quantity: 1,
    },
  });
  emitter.setDepth(10); // above both battler sprites, below DOM chrome
  // Ring skins expand evenly — uniform radial speed reads as a pulse.
  emitter.explode(
    Math.min(20, Math.ceil(plan.count * FX_COUNT_BOOST)),
    0,
    0,
  );
  scene.time.delayedCall(lifespan + 250, () => emitter.destroy());
  return true;
}

/**
 * Hit-stop: freeze every scene tween + sprite animation for `ms`, then
 * resume. WHY THIS CANNOT DEADLOCK THE EVENT LOOP: the battle's async
 * sequencing (BattleScene.wait, the LearnedMovesModal awaits, victory
 * timelines) runs on scene.time.delayedCall — the Clock plugin — which
 * `tweens.pauseAll()` / `anims.pauseAll()` do not touch. The resume is
 * itself a Clock delayedCall, so even if nothing was paused it fires
 * and no-ops. Worst case under scene teardown: SHUTDOWN destroys the
 * Clock along with the paused tweens — nothing left to resume.
 */
export function hitStop(scene: Phaser.Scene, ms: number): void {
  scene.tweens.pauseAll();
  scene.anims.pauseAll();
  scene.time.delayedCall(ms, () => {
    scene.tweens.resumeAll();
    scene.anims.resumeAll();
  });
}
